import { Link, useLocation, useNavigate } from 'react-router-dom';

/**
 * BackHomeNav — strip horizontal con dos botones (← Volver / 🏠 Inicio)
 * que aparece en TODAS las páginas excepto `/`. ROUND10 #6.
 *
 * Montado una sola vez en `AppShell`. Las páginas no necesitan modificarse
 * — la consistencia es automática.
 *
 * Diseño: `navigate(-1)` usa el browser history (NO toca el estado del
 * carrito ni la state machine de checkout — explícitamente requerido por
 * la orden ROUND10). Si no hay historial (deep-link directo) el botón
 * Home queda al lado para que el usuario siempre tenga una salida.
 */

function ArrowLeftIcon(): JSX.Element {
  return (
    <svg
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 12H5" />
      <path d="m12 19-7-7 7-7" />
    </svg>
  );
}

function HomeIcon(): JSX.Element {
  return (
    <svg
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

export function BackHomeNav(): JSX.Element | null {
  const location = useLocation();
  const navigate = useNavigate();

  if (location.pathname === '/') return null;

  return (
    // Overlay transparente sobre el tope del banner (montado dentro de `main`
    // relative en AppShell). Sin fondo blanco ni borde: se integra al banner.
    // Botones con píldora translúcida oscura + texto claro → legibles tanto sobre
    // banners oscuros como sobre páginas de fondo claro.
    <nav aria-label="Navegación secundaria" className="absolute inset-x-0 top-0 z-30">
      <div className="container-fc flex items-center gap-2 py-3 text-sm">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 rounded-full bg-black/25 px-3 py-1.5 text-white/90 backdrop-blur-sm transition-colors hover:bg-black/45 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          aria-label="Volver a la página anterior"
        >
          <ArrowLeftIcon />
          <span>Volver</span>
        </button>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 rounded-full bg-black/25 px-3 py-1.5 text-white/90 backdrop-blur-sm transition-colors hover:bg-black/45 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          aria-label="Ir al inicio"
        >
          <HomeIcon />
          <span>Inicio</span>
        </Link>
      </div>
    </nav>
  );
}
